/**
 * Publish an Ask AI answer as a report (publish-report spec, F1; refreshable-
 * report spec, F2).
 *
 * F2 turns the dialog into a short flow for refreshable ("rerun") reports:
 *
 *   Question  FINN drafts ONE self-contained question from the conversation
 *             (draftReportQuestion); the publisher edits it.
 *   Running   The question is run fresh in its own thread with the
 *             report-format skill (startReportRun); the browser polls the turn.
 *   Review    The fresh answer is shown; publish it, edit the question, or
 *             fall back to the original answer.
 *   Details   Title / description / visibility (the F1 fields).
 *   Published Link to the report.
 *
 * "Publish snapshot instead" (and every PDF-based answer, Req 2.7) takes the
 * F1 path: the chat answer is stored as-is. The already-published case is the
 * F1 edit / unpublish form, unchanged. Flow state is the pure reducer in
 * publishFlow.ts; run polling lives in reportRun.ts.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { marked, parse } from "marked";
import { generateAuditedClient } from "../../audit/auditedClient";
import {
  publishReport,
  startReportRun,
  unpublishReport,
} from "../../graphql/mutations";
import {
  draftReportQuestion,
  getPublishedReportsSummary,
} from "../../graphql/queries";
import type {
  DraftReportQuestionInput,
  GetPublishedReportsInput,
  PublishedReportSource,
  PublishedReportSummary,
  PublishReportInput,
  StartReportRunInput,
  UnpublishReportInput,
} from "../../models/API";
import AnswerTableDownloads from "../csvDownload/AnswerTableDownloads";
import {
  allowedVisibilities,
  buildProvenance,
  buildRunScope,
  DEFAULT_RUN_TIMEOUT_SECONDS,
  DEFINING_QUESTION_MAX_LENGTH,
  defaultTitleFor,
  formatElapsed,
  publishedReportDeepLink,
  ReportVisibility,
  threadOf,
  TITLE_MAX_LENGTH,
  validateDefiningQuestion,
  validateTitle,
  visibilityFlags,
  visibilityOf,
  VISIBILITY_LABELS,
} from "./publishedReportModel";
import {
  initialPublishFlowState,
  publishFlowReducer,
  RunHandle,
} from "./publishFlow";
import {
  buildRecordRunInput,
  pollReportRun,
  recordRun,
  RunOutcome,
} from "./reportRun";

marked.setOptions({ breaks: true, gfm: true });

/** The completed answer being published, as the chat holds it. */
export interface PublishTarget {
  /** Source turn id ({thread}#{epoch}#{uuid}). */
  conversationId: string;
  question: string;
  /** Marker-stripped answer markdown exactly as the chat rendered it. */
  markdown: string;
  toolCalls?: any[];
  sources?: any[];
  requestScope?: PublishedReportSource["request_scope"];
  /** True when the answer was produced from an attached PDF: it cannot be re-run (Req 2.7). */
  pdf?: boolean;
}

interface PublishReportDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  teamId: number;
  teamName: string;
  effectiveRole: string;
  target: PublishTarget | null;
  /** Fired after a successful publish, edit or unpublish. */
  onChanged?: (report: PublishedReportSummary | null) => void;
  /** How long to wait for the publish-time run (the chat's request timeout). */
  runTimeoutSeconds?: number;
  /** Poll interval for the run turn; tests shorten it. */
  pollIntervalMs?: number;
}

// First AppSync error message, else the fallback (same helper as ShareLinkDialog).
function errorMessageOf(err: any, fallback: string): string {
  const message = err?.errors?.[0]?.message ?? err?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

const RERUN_STEPS = ["Question", "Run & review", "Details"];

const PublishReportDialog: React.FC<PublishReportDialogProps> = ({
  open,
  onClose,
  userId,
  teamId,
  teamName,
  effectiveRole,
  target,
  onChanged,
  runTimeoutSeconds = DEFAULT_RUN_TIMEOUT_SECONDS,
  pollIntervalMs = 2000,
}) => {
  const [state, dispatch] = useReducer(
    publishFlowReducer,
    initialPublishFlowState
  );
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<ReportVisibility>("private");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  // Async work from a previous open (or a cancelled run) must not touch the
  // current flow: every async path captures the session it started in.
  const sessionRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const client = useMemo(() => generateAuditedClient(), []);
  const allowed = allowedVisibilities(effectiveRole, teamId);
  const titleError = validateTitle(title);
  const questionError = validateDefiningQuestion(state.question);
  const { step, mode, existing } = state;

  const resetFields = useCallback((question: string) => {
    setTitle(defaultTitleFor(question));
    setTitleTouched(false);
    setDescription("");
    setVisibility("private");
    setError(null);
  }, []);

  // "Already published?" pre-check (own rows only, any team; Req 1.4, 1.5),
  // then either the F1 edit form, the snapshot details (PDF), or the draft.
  const startFlow = useCallback(
    async (session: number, t: PublishTarget) => {
      let found: PublishedReportSummary | null = null;
      try {
        const input: GetPublishedReportsInput = {
          user_id: userId,
          team_id: teamId,
          conversation_id: t.conversationId,
        };
        const result = await client.graphql({
          query: getPublishedReportsSummary,
          variables: { input },
        });
        if (session !== sessionRef.current) return;
        found = (result.data.getPublishedReports ?? [])[0] ?? null;
      } catch (err) {
        // Publishing is still possible; the server enforces uniqueness.
        console.error("[PublishReportDialog] pre-check failed:", err);
        if (session !== sessionRef.current) return;
      }
      if (found) {
        dispatch({ type: "precheck_found", report: found });
        setTitle(found.title);
        setDescription(found.description ?? "");
        setVisibility(visibilityOf(found));
        return;
      }
      dispatch({ type: "precheck_none", pdf: !!t.pdf });
      if (t.pdf) return;
      try {
        const input: DraftReportQuestionInput = {
          user_id: userId,
          thread: threadOf(t.conversationId),
          source_turn_id: t.conversationId,
        };
        const result = await client.graphql({
          query: draftReportQuestion,
          variables: { input },
        });
        if (session !== sessionRef.current) return;
        const draft = result.data.draftReportQuestion;
        if (!draft) throw new Error("No draft returned");
        dispatch({ type: "draft_ready", draft });
      } catch (err) {
        console.error("[PublishReportDialog] drafting failed:", err);
        if (session !== sessionRef.current) return;
        dispatch({ type: "draft_failed" });
        dispatch({ type: "edit_question", question: t.question });
      }
    },
    [client, userId, teamId]
  );

  useEffect(() => {
    if (!open || !target) return;
    const session = ++sessionRef.current;
    dispatch({ type: "reset" });
    resetFields(target.question);
    startFlow(session, target);
    return () => {
      sessionRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.conversationId]);

  // Elapsed-time display while a run is in flight.
  useEffect(() => {
    if (step !== "running" || !state.run) return;
    const startedAt = state.run.startedAt;
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [step, state.run]);

  const waitForRun = useCallback(
    async (session: number, run: RunHandle) => {
      const controller = new AbortController();
      abortRef.current = controller;
      const outcome = await pollReportRun(client, userId, run.runTurnId, {
        timeoutMs: runTimeoutSeconds * 1000,
        intervalMs: pollIntervalMs,
        signal: controller.signal,
      });
      if (session !== sessionRef.current || controller.signal.aborted) return;
      abortRef.current = null;
      dispatch({ type: "run_finished", outcome });
    },
    [client, userId, runTimeoutSeconds, pollIntervalMs]
  );

  const handleRun = async () => {
    if (!target || busy || questionError) return;
    setBusy(true);
    setError(null);
    const session = sessionRef.current;
    try {
      const input: StartReportRunInput = {
        user_id: userId,
        question: state.question.trim(),
        run_scope: buildRunScope(target.requestScope, teamId),
      };
      const result = await client.graphql({
        query: startReportRun,
        variables: { input },
      });
      if (session !== sessionRef.current) return;
      const started = result.data.startReportRun;
      if (!started) throw new Error("No run returned");
      const run: RunHandle = {
        runThread: started.run_thread,
        runTurnId: started.run_turn_id,
        startedAt: Date.now(),
      };
      dispatch({ type: "run_started", run });
      setBusy(false);
      await waitForRun(session, run);
    } catch (err) {
      console.error("[PublishReportDialog] run failed to start:", err);
      if (session !== sessionRef.current) return;
      setError(
        errorMessageOf(err, "Could not start the report run. Please try again.")
      );
      setBusy(false);
    }
  };

  const handleKeepWaiting = () => {
    if (!state.run) return;
    dispatch({ type: "run_started", run: state.run });
    waitForRun(sessionRef.current, state.run);
  };

  const handleCancelWait = () => {
    // The run keeps going server-side and lands in Ask AI history when done.
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: "cancel_wait" });
  };

  const handleToDetails = () => {
    if (!titleTouched) setTitle(defaultTitleFor(state.question));
    dispatch({ type: "to_details" });
  };

  const handleUseSnapshot = () => {
    if (!titleTouched && target) setTitle(defaultTitleFor(target.question));
    dispatch({ type: "use_snapshot" });
  };

  const handlePublish = async () => {
    if (!target || titleError || busy) return;
    const outcome =
      mode === "rerun" && state.outcome?.status === "succeeded"
        ? (state.outcome as Extract<RunOutcome, { status: "succeeded" }>)
        : null;
    if (mode === "rerun" && !outcome) return;
    setBusy(true);
    setError(null);
    const session = sessionRef.current;
    try {
      const runScope = buildRunScope(target.requestScope, teamId);
      const definingQuestion = state.question.trim();
      const input: PublishReportInput = {
        team_id: teamId,
        user_id: userId,
        title: title.trim(),
        description: description.trim() || null,
        ...visibilityFlags(visibility),
        conversation_id: outcome ? outcome.runTurnId : target.conversationId,
        question: outcome ? definingQuestion : target.question,
        content: (outcome ? outcome.text : target.markdown).trimEnd(),
        source: JSON.stringify(
          outcome
            ? buildProvenance({
                requestScope: outcome.requestScope ?? target.requestScope,
                toolCalls: outcome.toolCalls,
                sources: outcome.sources,
                originalQuestion: target.question,
                originalTurnId: target.conversationId,
              })
            : buildProvenance({
                requestScope: target.requestScope,
                toolCalls: target.toolCalls,
                sources: target.sources,
              })
        ),
        refresh_mode: outcome ? "rerun" : "snapshot",
      };
      const result = await client.graphql({
        query: publishReport,
        variables: { input },
      });
      if (session !== sessionRef.current) return;
      const report = result.data.publishReport;
      if (!report) throw new Error("No report returned");
      if (outcome) {
        // The publish-time run becomes the report's first run (Req 3.1). The
        // report is already published; a failure here only loses the run row.
        try {
          await recordRun(
            client,
            buildRecordRunInput({
              userId,
              reportId: report.id,
              outcome,
              question: definingQuestion,
              runScope,
            })
          );
        } catch (err) {
          console.error("[PublishReportDialog] recording the run failed:", err);
        }
        if (session !== sessionRef.current) return;
      }
      dispatch({ type: "published", report });
      onChanged?.(report);
    } catch (err) {
      console.error("[PublishReportDialog] publish failed:", err);
      if (session !== sessionRef.current) return;
      setError(
        errorMessageOf(err, "Could not publish this answer. Please try again.")
      );
    } finally {
      if (session === sessionRef.current) setBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!existing || titleError || busy) return;
    setBusy(true);
    setError(null);
    try {
      const input: PublishReportInput = {
        id: existing.id,
        team_id: existing.team_id,
        user_id: userId,
        title: title.trim(),
        description: description.trim() || null,
        ...visibilityFlags(visibility),
      };
      const result = await client.graphql({
        query: publishReport,
        variables: { input },
      });
      const report = result.data.publishReport;
      if (!report) throw new Error("No report returned");
      dispatch({ type: "precheck_found", report });
      setSnack("Report updated");
      onChanged?.(report);
    } catch (err) {
      console.error("[PublishReportDialog] edit failed:", err);
      setError(
        errorMessageOf(err, "Could not update this report. Please try again.")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleUnpublish = async () => {
    if (!existing || busy || !target) return;
    setBusy(true);
    setError(null);
    try {
      const input: UnpublishReportInput = { id: existing.id, user_id: userId };
      const result = await client.graphql({
        query: unpublishReport,
        variables: { input },
      });
      if (!result.data.unpublishReport) throw new Error("No report returned");
      onChanged?.(null);
      setSnack("Report unpublished");
      // The answer can be published again now: restart the flow.
      const session = ++sessionRef.current;
      dispatch({ type: "reset" });
      resetFields(target.question);
      startFlow(session, target);
    } catch (err) {
      console.error("[PublishReportDialog] unpublish failed:", err);
      setError(
        errorMessageOf(
          err,
          "Could not unpublish this report. Please try again."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const published = state.published ?? existing;
  const deepLink = published ? publishedReportDeepLink(published) : "";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(deepLink);
      setSnack("Link copied to clipboard");
    } catch (err) {
      console.error("[PublishReportDialog] clipboard copy failed:", err);
      setSnack("Could not copy the link. Please copy it manually.");
    }
  };

  const handleOpen = () => {
    window.location.assign(deepLink);
  };

  // ---------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------

  const renderAnswer = (markdown: string, key: string) => (
    <Box
      className="ChatBoxComponent-message AI"
      sx={{ p: 1.5, borderRadius: 2, maxHeight: 420, overflowY: "auto" }}
    >
      <AnswerTableDownloads
        html={(parse(markdown) as string).replace(/>\s+</g, "><")}
        messageIndex={0}
        conversationId={key}
      />
    </Box>
  );

  const modeChip = (
    <Chip
      size="small"
      icon={mode === "rerun" ? <AutorenewIcon /> : undefined}
      label={mode === "rerun" ? "Refreshable" : "Snapshot"}
      color={mode === "rerun" ? "primary" : "default"}
      variant="outlined"
    />
  );

  const visibilityField = (
    <FormControl component="fieldset">
      <FormLabel
        component="legend"
        sx={{ fontWeight: 500, fontSize: "0.875rem" }}
      >
        Visibility
      </FormLabel>
      <RadioGroup
        value={visibility}
        onChange={(e) => setVisibility(e.target.value as ReportVisibility)}
        aria-label="visibility"
      >
        {(["private", "public", "global"] as ReportVisibility[]).map((v) => {
          const enabled =
            step === "existing" && existing
              ? // Editing: the rules apply to the report's own team.
                allowedVisibilities(effectiveRole, existing.team_id).includes(v)
              : allowed.includes(v);
          const control = (
            <FormControlLabel
              key={v}
              value={v}
              control={<Radio size="small" disabled={!enabled} />}
              label={VISIBILITY_LABELS[v]}
              disabled={!enabled}
            />
          );
          if (enabled) return control;
          const why =
            v === "global"
              ? "Only FINN team operators/admins can publish global reports"
              : "Only operators/admins can publish to the team";
          return (
            <Tooltip key={v} title={why} placement="right">
              <span>{control}</span>
            </Tooltip>
          );
        })}
      </RadioGroup>
    </FormControl>
  );

  const editableFields = (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        autoFocus
        label="Title"
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          setTitleTouched(true);
        }}
        fullWidth
        required
        error={title.length > 0 && !!titleError}
        helperText={
          title.length > 0 && titleError
            ? titleError
            : `${title.trim().length}/${TITLE_MAX_LENGTH} characters`
        }
        inputProps={{ maxLength: TITLE_MAX_LENGTH + 20 }}
      />
      <TextField
        label="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        minRows={2}
      />
      {visibilityField}
      <Typography variant="body2" color="text.secondary">
        Team:{" "}
        <strong>
          {step === "existing" && existing
            ? existing.team_name ?? teamName
            : teamName}
        </strong>
      </Typography>
    </Stack>
  );

  const previewQuestion =
    mode === "rerun" ? state.question : target?.question ?? "";
  const previewMarkdown =
    mode === "rerun" && state.outcome?.status === "succeeded"
      ? state.outcome.text
      : target?.markdown ?? "";

  const preview = target && (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ mt: 2, "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Preview report
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, whiteSpace: "pre-wrap", mb: 1 }}
        >
          {previewQuestion}
        </Typography>
        <Box
          sx={{ maxHeight: 320, overflowY: "auto", fontSize: "0.9rem" }}
          dangerouslySetInnerHTML={{
            __html: (parse(previewMarkdown) as string).replace(/>\s+</g, "><"),
          }}
        />
      </AccordionDetails>
    </Accordion>
  );

  const stepperIndex =
    step === "question"
      ? 0
      : step === "running" || step === "review"
      ? 1
      : step === "details"
      ? 2
      : -1;
  const showStepper = mode === "rerun" && stepperIndex >= 0;

  const dialogTitle =
    step === "existing"
      ? "Already published"
      : step === "published"
      ? "Report published"
      : "Publish answer as a report";

  const questionStep = (
    <>
      <Typography variant="body2" color="text.secondary">
        A refreshable report re-asks one self-contained question every time it
        is refreshed. FINN drafts that question from this conversation; make
        sure it stands on its own, then run it to see the report.
      </Typography>
      {state.drafting ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <span>Drafting a standalone question from this conversation…</span>
        </Box>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {state.draft === null && (
            <Alert severity="warning">
              FINN couldn&apos;t draft a question automatically. Below is the
              last question you asked; edit it so it stands on its own.
            </Alert>
          )}
          {state.draft?.fallback && (
            <Alert severity="info">
              FINN couldn&apos;t draft a standalone question
              {state.draft.reason ? ` (${state.draft.reason})` : ""}. Below is
              the last question you asked; edit it so it stands on its own.
            </Alert>
          )}
          <TextField
            label="Report question"
            value={state.question}
            onChange={(e) =>
              dispatch({ type: "edit_question", question: e.target.value })
            }
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            error={state.question.length > 0 && !!questionError}
            helperText={
              state.question.length > 0 && questionError
                ? questionError
                : `${
                    state.question.trim().length
                  }/${DEFINING_QUESTION_MAX_LENGTH} characters` +
                  (state.draft && !state.draft.fallback
                    ? ` · drafted from ${state.draft.basis_turns} turn${
                        state.draft.basis_turns === 1 ? "" : "s"
                      }`
                    : "")
            }
          />
        </Stack>
      )}
    </>
  );

  const runningStep = (
    <Stack spacing={1.5} sx={{ py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">
          Running the report question… {formatElapsed(elapsed)}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, whiteSpace: "pre-wrap" }}
      >
        {state.question}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        FINN is answering this question fresh, in the report format. Long
        analyses can take a few minutes; the run also appears in your Ask AI
        history.
      </Typography>
    </Stack>
  );

  const reviewStep = state.outcome && (
    <Stack spacing={1.5}>
      {state.outcome.status === "succeeded" && (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            {modeChip}
            <Typography variant="body2" color="text.secondary">
              This answer is what the report will show; refreshing re-runs the
              question.
            </Typography>
          </Stack>
          <Typography variant="subtitle2" color="text.secondary">
            Question
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {state.question}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Answer
          </Typography>
          {renderAnswer(
            state.outcome.text,
            `report-run-${state.outcome.runTurnId}`
          )}
        </>
      )}
      {state.outcome.status === "failed" && (
        <Alert severity="error">
          The run failed ({state.outcome.errorType}): {state.outcome.text}
        </Alert>
      )}
      {state.outcome.status === "timeout" && (
        <Alert severity="warning">
          The run is still going after {formatElapsed(runTimeoutSeconds)}. You
          can keep waiting, edit the question, or publish the original answer
          instead; the finished answer will appear in your Ask AI history either
          way.
        </Alert>
      )}
    </Stack>
  );

  const detailsStep = (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        {modeChip}
        <Typography variant="body2" color="text.secondary">
          {mode === "rerun"
            ? "This report re-runs its question whenever someone refreshes it."
            : target?.pdf
            ? "Answers based on an attached PDF are published as a snapshot of the answer."
            : "Publishing stores a snapshot of this answer as a report in the Dashboard section."}
        </Typography>
      </Stack>
      {editableFields}
      {preview}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={busy || step === "running" ? undefined : onClose}
      maxWidth={step === "review" ? "md" : "sm"}
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PublishOutlinedIcon fontSize="small" />
          <span>{dialogTitle}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {showStepper && (
          <Stepper activeStep={stepperIndex} alternativeLabel sx={{ mb: 2 }}>
            {RERUN_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {step === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <span>Checking this answer…</span>
          </Box>
        )}
        {step === "question" && questionStep}
        {step === "running" && runningStep}
        {step === "review" && reviewStep}
        {step === "details" && detailsStep}
        {step === "existing" && existing && (
          <>
            <Alert severity="info" sx={{ mb: 1 }}>
              This answer is already published as{" "}
              <strong>{existing.title}</strong> (
              {VISIBILITY_LABELS[visibilityOf(existing)]
                .split(" (")[0]
                .toLowerCase()}
              , team {existing.team_name ?? existing.team_id}). You can update
              it or unpublish it here.
            </Alert>
            {editableFields}
          </>
        )}
        {step === "published" && published && (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>{published.title}</strong> is now published to{" "}
              {VISIBILITY_LABELS[visibilityOf(published)]
                .split(" (")[0]
                .toLowerCase()}{" "}
              visibility in team {published.team_name ?? teamName}
              {mode === "rerun"
                ? " as a refreshable report."
                : " as a snapshot."}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Report link"
                value={deepLink}
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Tooltip title="Copy link">
                <IconButton
                  onClick={handleCopyLink}
                  color="primary"
                  aria-label="Copy report link"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 0.5 }}>
        {step === "loading" && <Button onClick={onClose}>Cancel</Button>}
        {step === "question" && (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleUseSnapshot} disabled={busy}>
              Publish snapshot instead
            </Button>
            <Button
              variant="contained"
              onClick={handleRun}
              disabled={busy || state.drafting || !!questionError}
              startIcon={
                busy ? <CircularProgress size={16} /> : <PlayArrowIcon />
              }
            >
              Run report
            </Button>
          </>
        )}
        {step === "running" && (
          <Button onClick={handleCancelWait}>Cancel waiting</Button>
        )}
        {step === "review" && state.outcome && (
          <>
            <Button onClick={() => dispatch({ type: "back_to_question" })}>
              Edit question
            </Button>
            <Button onClick={handleUseSnapshot}>
              Publish original answer instead
            </Button>
            {state.outcome.status === "succeeded" && (
              <Button variant="contained" onClick={handleToDetails}>
                Continue
              </Button>
            )}
            {state.outcome.status === "failed" && (
              <Button
                variant="contained"
                onClick={handleRun}
                startIcon={<PlayArrowIcon />}
              >
                Run again
              </Button>
            )}
            {state.outcome.status === "timeout" && (
              <Button variant="contained" onClick={handleKeepWaiting}>
                Keep waiting
              </Button>
            )}
          </>
        )}
        {step === "details" && (
          <>
            {mode === "rerun" ? (
              <Button
                onClick={() => dispatch({ type: "back_to_review" })}
                disabled={busy}
              >
                Back
              </Button>
            ) : (
              !target?.pdf && (
                <Button
                  onClick={() => dispatch({ type: "back_to_question" })}
                  disabled={busy}
                >
                  Back
                </Button>
              )
            )}
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePublish}
              disabled={busy || !!titleError}
              startIcon={
                busy ? <CircularProgress size={16} /> : <PublishOutlinedIcon />
              }
            >
              Publish
            </Button>
          </>
        )}
        {step === "existing" && (
          <>
            <Button onClick={handleUnpublish} color="error" disabled={busy}>
              Unpublish
            </Button>
            <Button
              onClick={handleOpen}
              startIcon={<OpenInNewIcon />}
              disabled={busy}
            >
              Open report
            </Button>
            <Button onClick={onClose} disabled={busy}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={busy || !!titleError}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
            >
              Save changes
            </Button>
          </>
        )}
        {step === "published" && (
          <>
            <Button onClick={handleOpen} startIcon={<OpenInNewIcon />}>
              Open report
            </Button>
            <Button onClick={onClose} variant="contained">
              Done
            </Button>
          </>
        )}
      </DialogActions>
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={snack ?? ""}
      />
    </Dialog>
  );
};

export default PublishReportDialog;
